// views/Dashboard/Settings/Settings.tsx
import { useEffect, useState, useCallback, type FormEvent, type ChangeEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Card from '../../../components/common/Card/Card';
import Button from '../../../components/common/Button/Button';
import Modal from '../../../components/common/Modal/Modal';
import Loading from '../../../components/common/Loading/Loading';
import PaymentView from '../../../components/common/PaymentView/PaymentView';
import { PushNotificationManager, User, Tier, Order } from '../../../Entities';
import { notify } from '../../../components/common/Toast/toastBridge';
import styles from './Settings.module.css';

interface Tab {
  id: string;
  label: string;
  icon: string;
}

const PASSWORD_RE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;
const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const Settings = () => {
  const { user, onPasswordChange, setUser, onEmailChange, isLoggedByEmail, onLogout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<string>(() =>
    new URLSearchParams(location.search).get('activeTab') === 'membership' ? 'membership' : 'profile',
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [tiers, setTiers] = useState<Tier[] | null>(null);
  const [userTier, setUserTier] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
  const [pushNotificationAllowed, setPushNotificationAllowed] = useState(PushNotificationManager.pushNotificationsSet());
  const [order, setOrder] = useState<Order | undefined>();

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage] = useState('');

  const [formData, setFormData] = useState<any>({
    name: user?.name,
    email: user?.email,
    professional: user?.professional,
    selectedTier: null,
    promoCode: null,
    promoCodeDiscount: null,
    promoCodeMonths: null,
    user,
  });

  // Clear promo sale after changing the selected tier.
  useEffect(() => {
    setFormData((prev: any) => ({ ...prev, promoCode: null, promoCodeDiscount: null, promoCodeMonths: null }));
  }, [formData.selectedTier]);

  const getTiers = useCallback(async () => {
    try {
      const all = await Tier.getAll();
      setTiers(all);
      setUserTier(all.filter((t) => t.name === user?.tier?.name)[0]);
      setLoading(false);
    } catch (error: any) {
      notify.error(error?.message || 'Nepodařilo se načíst data');
      navigate('/dashboard');
    }
  }, [user?.tier?.name, navigate]);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('result')) setShowPaymentModal(true);
    getTiers();
  }, [getTiers]);

  const countSale = (): number => {
    if (!user?.active_order) return 0;
    const currentTimestamp = Math.round(Date.now() / 1000);
    const { expiration_timestamp, creation_timestamp, last_paid_amount } = user.active_order;
    const unusedTimePerc = 1 - (currentTimestamp - creation_timestamp) / (expiration_timestamp - creation_timestamp);
    return Math.round(last_paid_amount * unusedTimePerc);
  };

  const getAvailableUpgrades = (): any[] => {
    if (!userTier || !tiers) return [];
    const availableTiers: any[] = [];
    for (let i = userTier.id + 1; i <= tiers.length; i++) {
      availableTiers.push(tiers.filter((t) => t.id === i)[0]);
    }
    return availableTiers;
  };

  const tabs: Tab[] = [
    { id: 'profile', label: 'Profil', icon: '👤' },
    ...(PushNotificationManager.supported ? [{ id: 'notifications', label: 'Notifikace', icon: '🔔' }] : []),
    { id: 'membership', label: 'Členství', icon: '💎' },
    ...(isLoggedByEmail() ? [{ id: 'security', label: 'Zabezpečení', icon: '🔒' }] : []),
    { id: 'documents', label: 'Dokumenty', icon: '📄' },
    { id: 'contacts', label: 'Kontakty', icon: '📞' },
  ];

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (name: string) => {
    setFormData((prev: any) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleUserInfoChange = async (e: FormEvent) => {
    e.preventDefault();
    if (!user) return;
    try {
      setProcessing(true);
      const updatedUser = new User(user);
      updatedUser.name = formData.name;
      updatedUser.professional = formData.professional;
      await updatedUser.update();
      setUser(updatedUser);
    } catch (error) {
      console.error(error);
      notify.error('Změna klientských nastavení se nezdařila');
    } finally {
      setProcessing(false);
    }
  };

  const handleNotificationSettings = async () => {
    try {
      setProcessing(true);
      setPushNotificationAllowed(await PushNotificationManager.toggle());
    } catch (error) {
      console.error(error);
    } finally {
      setProcessing(false);
    }
  };

  const handleOrderCreation = async () => {
    try {
      setProcessing(true);
      const created = await Order.create({ tierId: formData.selectedTier.id, subscriptionScope: 'settings' });
      setOrder(created);
      setShowUpgradeModal(false);
      setShowPaymentModal(true);
    } catch (error) {
      console.error(error);
      notify.error('Nepodařilo se vytvořit objednávku');
    } finally {
      setProcessing(false);
    }
  };

  const redirectBillingPortal = async () => {
    try {
      setProcessing(true);
      const managing_url = await Order.getOrderManagingUrl(window.location.href);
      window.location.replace(managing_url);
    } catch (error) {
      console.log(error);
      notify.error('Nastala chyba s přesměrováním na správu předplatného');
    } finally {
      setProcessing(false);
    }
  };

  const checkPasswordValidation = (currentPassword: string, newPassword: string, confirmPassword: string): boolean => {
    const newErrors: Record<string, string> = {};
    let allRight = true;
    if (!currentPassword) {
      newErrors.currentPassword = 'toto pole je povinné';
      allRight = false;
    }
    if (!newPassword) {
      newErrors.newPassword = 'toto pole je povinné';
      allRight = false;
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = 'toto pole je povinné';
      allRight = false;
    }
    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Nové heslo a potvrzení se neshodují.';
      allRight = false;
    }
    if (!PASSWORD_RE.test(newPassword)) {
      newErrors.newPassword = 'Heslo musí mít alespoň 8 znaků, velké a malé písmeno, číslo a speciální znak.';
      allRight = false;
    }
    setPasswordErrors({ ...newErrors });
    return allRight;
  };

  const handleEmailChange = async (e: FormEvent) => {
    e.preventDefault();
    try {
      setProcessing(true);
      if (formData.email === user?.email) return;
      if (!isValidEmail(formData.email)) {
        notify.warning('Neplatný formát emailu');
        return;
      }
      await onEmailChange(formData.email);
      notify.success('Změna emailu proběhla úspěšně. Budete odhlášeni, prosím přihlaste se znovu.');
      onLogout();
    } catch (error) {
      notify.error('Nepodařilo se změnit email');
    } finally {
      setProcessing(false);
    }
  };

  const changePasswordHandler = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    try {
      setProcessing(true);
      setPasswordErrors({});
      const currentPassword = (form.elements.namedItem('currentPassword') as HTMLInputElement).value.trim();
      const newPassword = (form.elements.namedItem('newPassword') as HTMLInputElement).value.trim();
      const confirmPassword = (form.elements.namedItem('confirmPassword') as HTMLInputElement).value.trim();

      if (!checkPasswordValidation(currentPassword, newPassword, confirmPassword)) return;

      await onPasswordChange({ currentPassword, newPassword });
      form.reset();
      setShowSuccessModal(true);
    } catch (error: any) {
      if (error?.message === 'Firebase: Error (auth/wrong-password).') {
        setPasswordErrors((prev) => ({ ...prev, currentPassword: 'nesprávné současné heslo', action: 'password' }));
      } else {
        setPasswordErrors((prev) => ({ ...prev, response: error?.message, action: 'password' }));
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setMobileMenuOpen(false);
  };

  const getCurrentTabInfo = () => tabs.find((tab) => tab.id === activeTab) ?? tabs[0];

  if (loading || !user || !userTier) return <Loading />;

  return (
    <div className={styles.settingsPage}>
      <h1>Nastavení</h1>

      <div className={styles.tabsContainer}>
        <div className={styles.desktopTabs} data-tour="settings-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
              onClick={() => setActiveTab(tab.id)}
              data-tour={`settings-tab-${tab.id}`}
            >
              <span className={styles.tabIcon}>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        <div className={styles.mobileTabSelector}>
          <button className={styles.mobileTabButton} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <span className={styles.tabIcon}>{getCurrentTabInfo().icon}</span>
            <span>{getCurrentTabInfo().label}</span>
            <span className={`${styles.arrow} ${mobileMenuOpen ? styles.open : ''}`}>▼</span>
          </button>

          {mobileMenuOpen && (
            <div className={styles.mobileTabMenu}>
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`${styles.mobileTabItem} ${activeTab === tab.id ? styles.active : ''}`}
                  onClick={() => handleTabChange(tab.id)}
                >
                  <span className={styles.tabIcon}>{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <Card className={styles.tabContent}>
          {activeTab === 'profile' && (
            <form onSubmit={handleUserInfoChange} className={styles.form}>
              <h2>Osobní údaje</h2>
              <div className={styles.formGroup}>
                <label htmlFor="name">Uživatelské jméno</label>
                <input type="text" id="name" name="name" className="form-input" value={formData.name} onChange={handleInputChange} disabled={processing} />
              </div>

              {(user.professional || user.bonus_revenue + user.referral_revenue >= 50000) && (
                <>
                  <h2>Nastavení profesionality</h2>
                  <div className={styles.formGroup}>
                    <div className={styles.checkboxItem}>
                      <div className={styles.checkboxInfo}>
                        <h4>Profesionalita</h4>
                        <p>Přijímám vyšší zisky za cenu vyššího rizika</p>
                      </div>
                      <label className={styles.switch}>
                        <input type="checkbox" checked={formData.professional} onChange={() => handleCheckboxChange('professional')} disabled={processing} />
                        <span className={styles.slider}></span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              <Button
                type="submit"
                variant={formData.name !== user.name || formData.professional !== user.professional ? 'primary' : 'secondary'}
                disabled={processing}
              >
                Uložit změny
              </Button>
            </form>
          )}

          {activeTab === 'notifications' && (
            <form onSubmit={handleUserInfoChange} className={styles.form}>
              <h2>Osobní údaje</h2>
              <div className={styles.formGroup}>
                <div className={styles.checkboxItem} data-tour="settings-notifications">
                  <div className={styles.checkboxInfo}>
                    <h4>Nové bonusy</h4>
                    <p>Upozornění na nové bonusy</p>
                  </div>
                  <label className={styles.switch}>
                    <input type="checkbox" checked={pushNotificationAllowed} onChange={handleNotificationSettings} disabled={processing} />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>
            </form>
          )}

          {activeTab === 'membership' && (
            <div className={styles.membershipTab}>
              <h2>Vaše členství</h2>

              <Card className={styles.currentPlan} data-tour="settings-plan">
                <div className={styles.planHeader}>
                  <div>
                    <h3>
                      Aktuální plán: <span className={styles.planName}>{userTier.name}</span>
                    </h3>
                    {userTier.name.toLowerCase() !== 'free' && (
                      <div className={styles.membershipDetails}>
                        <p>
                          Cena: <strong>{user.active_order.amount} CZK</strong>
                        </p>
                        <div className={styles.managingOrderBtn} data-tour="settings-billing">
                          <Button onClick={redirectBillingPortal} disabled={processing}>
                            Přesměrovat na portál správy předplatného
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.planActions}>
                  {userTier.name.toLowerCase() === 'free' && (
                    <Button variant="primary" onClick={() => setShowUpgradeModal(true)}>
                      Upgradovat členství
                    </Button>
                  )}
                </div>
              </Card>

              <div className={styles.membershipFeatures} data-tour="settings-features">
                <h3>Funkce vašeho členství:</h3>
                <div className={styles.featuresList}>
                  {userTier.features.map((feature: string, index: number) => (
                    <div key={index} className={styles.featureItem}>
                      <span className={styles.featureIcon}>✅</span>
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className={styles.securityTab}>
              <h2>Zabezpečení účtu</h2>

              <Card className={styles.secCard}>
                <h3 className={styles.subHeaderSecurity}>Změna emailu</h3>
                <form className={styles.form} onSubmit={handleEmailChange}>
                  <div className={styles.formGroup}>
                    <label htmlFor="email">Email</label>
                    <input type="email" id="email" name="email" className="form-input" value={formData.email} onChange={handleInputChange} disabled={!isLoggedByEmail()} />
                  </div>
                  <Button type="submit" variant="primary" disabled={processing}>
                    Změnit email
                  </Button>
                </form>
              </Card>

              <Card className={styles.secCard}>
                <h3 className={styles.subHeaderSecurity}>Změna hesla</h3>
                <form className={styles.form} onSubmit={changePasswordHandler}>
                  <div className={styles.formGroup}>
                    <label htmlFor="currentPassword">Současné heslo</label>
                    <input type="password" id="currentPassword" name="currentPassword" className="form-input" placeholder="••••••••" />
                    {passwordErrors.currentPassword && passwordErrors.action === 'password' && (
                      <span className={styles.errorText}>{passwordErrors.currentPassword}</span>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="newPassword">Nové heslo</label>
                    <input type="password" id="newPassword" name="newPassword" className="form-input" placeholder="••••••••" />
                    {passwordErrors.newPassword && <span className={styles.errorText}>{passwordErrors.newPassword}</span>}
                  </div>

                  <div className={styles.formGroup}>
                    <label htmlFor="confirmPassword">Potvrzení hesla</label>
                    <input type="password" id="confirmPassword" name="confirmPassword" className="form-input" placeholder="••••••••" />
                    {passwordErrors.confirmPassword && <span className={styles.errorText}>{passwordErrors.confirmPassword}</span>}
                  </div>

                  <div className={styles.passwordStrength}>
                    <h4>Požadavky na heslo:</h4>
                    <ul>
                      <li>Minimálně 8 znaků</li>
                      <li>Velké a malé písmeno</li>
                      <li>Alespoň 1 číslo</li>
                      <li>Speciální znak</li>
                    </ul>
                  </div>

                  {passwordErrors.response && (
                    <span style={{ margin: '17px' }} className={styles.errorText}>
                      {passwordErrors.response}
                    </span>
                  )}
                  <Button type="submit" variant="primary" disabled={processing}>
                    Změnit heslo
                  </Button>
                </form>
              </Card>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className={styles.subtab}>
              <h2>Dokumenty</h2>
              <ul>
                <li className={styles.docsList}>
                  <a href="https://src.algapia.cz/docs/VOP.pdf" target="_blank" rel="noopener noreferrer">
                    Všeobecné obchodní podmínky
                  </a>
                </li>
                <li>
                  <a href="https://src.algapia.cz/docs/GDPR.pdf" target="_blank" rel="noopener noreferrer">
                    GDPR
                  </a>
                </li>
              </ul>
            </div>
          )}

          {activeTab === 'contacts' && (
            <div className={styles.subtab}>
              <h2>Kontakty</h2>
              <p>
                V případě jakýchkoliv dotazu nás neváhejte kontaktovat na emailové adrese: <strong>algapia@algapia.cz</strong>
              </p>
            </div>
          )}
        </Card>
      </div>

      {/* Upgrade modal */}
      <Modal isOpen={showUpgradeModal && getAvailableUpgrades().length > 0} onClose={() => setShowUpgradeModal(false)} title="Upgradovat členství">
        <h3>Vyberte plán, na který chcete upgradovat:</h3>

        <div className={styles.planOptions}>
          {getAvailableUpgrades().map((plan) => (
            <div
              key={plan.id}
              onClick={() => setFormData((prev: any) => ({ ...prev, selectedTier: plan }))}
              className={`${styles.planOption} ${formData.selectedTier?.id === plan.id ? styles.selected : ''}`}
            >
              <div className={styles.planOptionInfo}>
                <h4>{plan.name}</h4>
                <div className={styles.price}>
                  {!user.used_free_trial && plan.free_trial_days !== 0 ? (
                    <span>
                      <del>{plan.price} Kč/měsíc</del>{' '}
                      <span style={{ color: '#90ee90' }}>
                        <i>{plan.free_trial_days} denní free trial</i>
                      </span>
                    </span>
                  ) : (
                    <span>{plan.price} Kč/měsíc</span>
                  )}
                </div>
                <div className={styles.price}>
                  {(() => {
                    let sale = countSale();
                    let discountedPrice = plan.price - sale;
                    if (discountedPrice < 0) {
                      discountedPrice = 0;
                      sale = plan.price;
                    }
                    if (sale) {
                      return `Vaše cena při upgradu: ${discountedPrice} CZK (sleva ${sale} CZK) na 1. měsíc, poté ${plan.price} CZK/měsíc`;
                    }
                    return null;
                  })()}
                </div>
                <ul className={styles.planFeatures}>
                  {plan.features.map((feature: string, index: number) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className={styles.modalActions}>
          <Button variant="secondary" onClick={() => setShowUpgradeModal(false)}>
            Zrušit
          </Button>
          <Button variant="primary" onClick={handleOrderCreation} className={styles.planOptionButton} disabled={processing || !formData.selectedTier}>
            Pokračovat
          </Button>
        </div>
      </Modal>

      {/* Payment modal */}
      <Modal isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)}>
        <div className={styles.upgradeModal}>
          <PaymentView formData={formData} order={order} title="Dokončení platby" subtitle="" />
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>
              Zrušit
            </Button>
          </div>
        </div>
      </Modal>

      {/* Success modal */}
      {showSuccessModal && (
        <Modal isOpen onClose={() => setShowSuccessModal(false)} title="Úspěch!">
          <div className={styles.successModal}>
            <div className={styles.successIcon}>✅</div>
            <p>{successMessage || 'Heslo bylo úspěšně změněno.'}</p>
            <div className={styles.modalActions}>
              <Button variant="primary" onClick={() => setShowSuccessModal(false)}>
                Pokračovat
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Settings;
